import { State } from '@meraki-internal/state';
import { NewCareMapWizard } from '../wizard/mini-wizard/wizards/NewCareMapWizard';
import { RecordingState } from '../recording/RecordingState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareMapState } from './CareMapState';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { Logger } from '../support/Logger';
import { RECORDING_ABORTED } from '../recording/RecordingService';

interface IAddCareRecipientLauncherState {
    isChoiceOpen: boolean;
}

export class AddCareRecipientLauncher extends State<IAddCareRecipientLauncherState> {
    static inject = () => [
        NewCareMapWizard,
        RecordingState,
        MixPanelEventEmitter,
        CareMapState,
        BufferedEventBus,
        Logger
    ];

    // The caremap chooseVoice() created (only set when we created one — not when we reused
    // an existing incomplete). Cleared when a recording commits or when the flow aborts.
    private pendingCareMapId?: string;

    // The caremap that was active when chooseVoice() ran, used to restore active state if the
    // voice flow aborts. Only meaningful alongside pendingCareMapId.
    private previousCareMapId?: string;

    constructor(
        private newCareMapWizard: NewCareMapWizard,
        private recordingState: RecordingState,
        private mixpanel: MixPanelEventEmitter,
        private careMapState: CareMapState,
        private events: BufferedEventBus,
        private logger: Logger
    ) {
        super({ isChoiceOpen: false });

        this.events.on(RECORDING_ABORTED, () => {
            // Self-filter: act only if we have a caremap to roll back. Other recording flows
            // (regular notes, first-time intro, etc.) emit the same event but leave us untouched.
            if (this.pendingCareMapId) {
                this.rollbackPendingCareMap().catch(err => {
                    this.logger.error(`AddCareRecipientLauncher: rollback failed: ${err?.toString?.() ?? err}`);
                });
            }
        });

        this.events.on('RECORDING_SAVED_SUCCESS', (payload: { summaryPrompt?: 'intro' | 'appointment' } | undefined) => {
            if (payload?.summaryPrompt === 'intro') {
                this.pendingCareMapId = undefined;
                this.previousCareMapId = undefined;
            }
        });
    }

    launch = () => {
        // When an earlier intro recording already extracted profile data on an incomplete
        // caremap (diagnoses/allergies/medications/address but no name yet), opening the
        // Voice/Type drawer is unsafe: picking Voice would re-record into the same caremap
        // and overwrite the extracted data when the new recording commits. Skip the drawer
        // and resume the wizard so the user can supply the missing name without losing what
        // we already captured. A truly blank incomplete caremap still gets the drawer — a
        // re-record there has nothing to overwrite.
        const incomplete = this.careMapState.getIncompleteCareMap();
        if (incomplete && this.careMapState.hasExtractedData(incomplete)) {
            this.newCareMapWizard.start();
            return;
        }
        this.setState({ isChoiceOpen: true });
    };

    cancel = () => {
        this.setState({ isChoiceOpen: false });
    };

    chooseVoice = async () => {
        this.setState({ isChoiceOpen: false });
        this.mixpanel.track('Add Care Recipient Method Selected', () => ({
            Method: 'voice'
        }));

        try {
            // Mirror NewCareMapWizard.create()'s entry behavior so the recording lands on the right
            // caremap: reuse a leftover incomplete one if present, otherwise create a fresh one.
            const incomplete = this.careMapState.getIncompleteCareMap();
            if (incomplete) {
                await this.careMapState.setActiveCaremap(incomplete.careMapId);
            } else {
                const previousCareMapId = this.careMapState.getActiveCaremap()?.careMapId;
                const { careMapId } = await this.careMapState.addNewCaremapV3({
                    patient: {},
                    isRecipientSelf: false,
                    isAutoCreated: true
                });
                await this.careMapState.setActiveCaremap(careMapId);
                // Set tracking refs only after both create + setActive succeed. If setActive
                // fails, the orphan is left to be picked up next time.
                this.previousCareMapId = previousCareMapId;
                this.pendingCareMapId = careMapId;
            }
        } catch (err: any) {
            this.logger.error(err, { message: 'AddCareRecipientLauncher: failed to set up caremap for voice flow' });
            return;
        }

        this.recordingState.triggerRecording({
            initiatedFrom: 'Add Care Recipient',
            summaryPrompt: 'intro'
        });
    };

    chooseType = () => {
        this.setState({ isChoiceOpen: false });
        this.mixpanel.track('Add Care Recipient Method Selected', () => ({
            Method: 'type'
        }));
        this.newCareMapWizard.start();
    };

    private rollbackPendingCareMap = async () => {
        const careMapId = this.pendingCareMapId;
        const previousId = this.previousCareMapId;
        this.pendingCareMapId = undefined;
        this.previousCareMapId = undefined;

        if (!careMapId) {
            return;
        }

        await this.careMapState.deleteCaremapById(careMapId);
        if (previousId) {
            await this.careMapState.setActiveCaremap(previousId);
        } else {
            await this.careMapState.unsetCurrentCareMap();
        }
    };
}
